import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BADGES, Badge, UserBadge } from '@/types/badges';
import type { PR } from '@/types/evolve';

interface UseBadgesProps {
  userId?: string;
  daysActive: number;
  totalWorkouts: number;
  prs: PR[];
  isPremium: boolean;
}

// Helper to get shown badges from sessionStorage
const getShownBadgesFromSession = (): Set<string> => {
  try {
    const stored = sessionStorage.getItem('evolve_shown_badges_session');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

// Helper to add badge to sessionStorage
const addShownBadgeToSession = (badgeId: string) => {
  try {
    const current = getShownBadgesFromSession();
    current.add(badgeId);
    sessionStorage.setItem('evolve_shown_badges_session', JSON.stringify([...current]));
  } catch {
    // Ignore sessionStorage errors
  }
};

export const useBadges = ({ userId, daysActive, totalWorkouts, prs, isPremium }: UseBadgesProps) => {
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [newlyUnlocked, setNewlyUnlocked] = useState<Badge | null>(null);
  const [loading, setLoading] = useState(true);
  // Keep this useState for hook order consistency (sessionStorage is used for actual tracking)
  const [_shownBadgesThisSession] = useState<Set<string>>(new Set());

  // Fetch user's unlocked badges
  const fetchBadges = useCallback(async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      setUserBadges(data || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Award a badge to the user
  const awardBadge = useCallback(async (badgeId: string): Promise<boolean> => {
    if (!userId) return false;

    // Check if already has badge
    const existing = userBadges.find(b => b.badge_id === badgeId);
    if (existing) return false;

    try {
      const { data, error } = await supabase
        .from('user_badges')
        .insert({
          user_id: userId,
          badge_id: badgeId,
          notified: false,
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setUserBadges(prev => [...prev, data]);

      // Trigger notification
      const badge = BADGES.find(b => b.id === badgeId);
      if (badge) {
        setNewlyUnlocked(badge);
      }

      return true;
    } catch (error) {
      console.error('Error awarding badge:', error);
      return false;
    }
  }, [userId, userBadges]);

  // Mark badge notification as seen
  const markNotified = useCallback(async (badgeId: string) => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('user_badges')
        .update({ notified: true })
        .eq('user_id', userId)
        .eq('badge_id', badgeId);

      if (error) {
        console.error('Error marking badge notified:', error);
        return;
      }

      setUserBadges(prev => 
        prev.map(b => b.badge_id === badgeId ? { ...b, notified: true } : b)
      );
    } catch (error) {
      console.error('Error marking badge notified:', error);
    }
  }, [userId]);

  // Dismiss the unlock modal
  const dismissUnlock = useCallback(() => {
    if (newlyUnlocked) {
      // Track that we've shown this badge this session using sessionStorage
      addShownBadgeToSession(newlyUnlocked.id);
      markNotified(newlyUnlocked.id);
    }
    setNewlyUnlocked(null);
  }, [newlyUnlocked, markNotified]);

  // Check and award badges based on current stats
  const checkBadges = useCallback(async () => {
    if (!userId) return;

    const earnedIds = userBadges.map(b => b.badge_id);

    // First Blood - First workout
    if (totalWorkouts >= 1 && !earnedIds.includes('first-blood')) {
      await awardBadge('first-blood');
      return; // Only award one badge at a time
    }

    // 7-Day Streak
    if (daysActive >= 7 && !earnedIds.includes('7-day-streak')) {
      await awardBadge('7-day-streak');
      return;
    }

    // PR Assassin - Any PR logged (if prs array has items)
    if (prs.length > 0 && !earnedIds.includes('pr-assassin')) {
      await awardBadge('pr-assassin');
      return;
    }

    // 30-Day Immortal
    if (daysActive >= 30 && !earnedIds.includes('30-day-immortal')) {
      await awardBadge('30-day-immortal');
      return;
    }

    // Century Club - 100 workouts
    if (totalWorkouts >= 100 && !earnedIds.includes('century-club')) {
      await awardBadge('century-club');
      return;
    }

    // Double Century - 200 workouts
    if (totalWorkouts >= 200 && !earnedIds.includes('double-century')) {
      await awardBadge('double-century');
      return;
    }

    // Legend - Day 90
    if (daysActive >= 90 && !earnedIds.includes('legend')) {
      await awardBadge('legend');
      return;
    }

    // Titan - Day 180
    if (daysActive >= 180 && !earnedIds.includes('titan')) {
      await awardBadge('titan');
      return;
    }

    // Immortal - Day 365
    if (daysActive >= 365 && !earnedIds.includes('immortal')) {
      await awardBadge('immortal');
      return;
    }

    // Ascended - Day 545+
    if (daysActive >= 545 && !earnedIds.includes('ascended')) {
      await awardBadge('ascended');
      return;
    }

    // Ascended God - Day 1000+
    if (daysActive >= 1000 && !earnedIds.includes('ascended-god')) {
      await awardBadge('ascended-god');
      return;
    }

    // Check big-3 total gains (bench, squat, deadlift)
    const big3Lifts = ['bench press', 'squat', 'deadlift', 'bench', 'back squat', 'front squat'];
    const big3PRs = prs.filter(pr => 
      big3Lifts.some(lift => pr.lift.toLowerCase().includes(lift))
    );
    
    // Calculate total weight (simplified - just sum current PRs)
    const totalBig3 = big3PRs.reduce((sum, pr) => sum + pr.weight, 0);

    // Check for plate breaker (405+ on squat or deadlift)
    const hasPlateBreaker = prs.some(pr => {
      const isSquatOrDeadlift = pr.lift.toLowerCase().includes('squat') || 
                                pr.lift.toLowerCase().includes('deadlift');
      return isSquatOrDeadlift && pr.weight >= 405;
    });

    if (hasPlateBreaker && !earnedIds.includes('plate-breaker')) {
      await awardBadge('plate-breaker');
      return;
    }

    // Weight clubs (based on total)
    if (totalBig3 >= 1000 && !earnedIds.includes('1000lb-club')) {
      await awardBadge('1000lb-club');
      return;
    }
    if (totalBig3 >= 750 && !earnedIds.includes('750lb-club')) {
      await awardBadge('750lb-club');
      return;
    }
    if (totalBig3 >= 500 && !earnedIds.includes('500lb-club')) {
      await awardBadge('500lb-club');
      return;
    }
    if (totalBig3 >= 250 && !earnedIds.includes('250lb-club')) {
      await awardBadge('250lb-club');
      return;
    }
    if (totalBig3 >= 100 && !earnedIds.includes('100lb-club')) {
      await awardBadge('100lb-club');
      return;
    }
  }, [userId, userBadges, daysActive, totalWorkouts, prs, awardBadge]);

  // Check for unnotified badges on load - but only show once per session
  useEffect(() => {
    if (!loading && userBadges.length > 0) {
      // Get shown badges from sessionStorage
      const shownBadges = getShownBadgesFromSession();
      
      // Find first unnotified badge that hasn't been shown this session
      const unnotified = userBadges.find(b => 
        !b.notified && !shownBadges.has(b.badge_id)
      );
      if (unnotified) {
        const badge = BADGES.find(b => b.id === unnotified.badge_id);
        if (badge) {
          setNewlyUnlocked(badge);
        }
      }
    }
  }, [loading, userBadges]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  // Check badges when stats change
  useEffect(() => {
    if (!loading && userId) {
      checkBadges();
    }
  }, [daysActive, totalWorkouts, prs.length, loading, userId]);

  const isBadgeUnlocked = (badgeId: string): boolean => {
    return userBadges.some(b => b.badge_id === badgeId);
  };

  const getBadgeUnlockDate = (badgeId: string): Date | null => {
    const badge = userBadges.find(b => b.badge_id === badgeId);
    return badge ? new Date(badge.unlocked_at) : null;
  };

  return {
    userBadges,
    badges: BADGES,
    newlyUnlocked,
    loading,
    isBadgeUnlocked,
    getBadgeUnlockDate,
    awardBadge,
    dismissUnlock,
    checkBadges,
    isPremium,
  };
};
